import type { Photo } from '@/lib/api';

export interface MonthGroup {
  /** `YYYY-MM`, used as a stable key. */
  key: string;
  label: string;
  photos: Photo[];
}

/**
 * Formats a `YYYY-MM` key for a heading.
 *
 * Built from the parts rather than `new Date(dateString)`, which parses a bare
 * date as UTC midnight and can render the previous month for anyone west of it.
 */
export function monthLabel(key: string, locale?: string): string {
  const [year, month] = key.split('-').map(Number);

  if (!year || !month) {
    return key;
  }

  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    new Date(year, month - 1, 1),
  );
}

/**
 * Groups an already-sorted feed into months, preserving order.
 *
 * The feed arrives newest first and paginates, so groups are built by walking
 * the list rather than by bucketing into a map, which would not survive a later
 * page appending to a month already on screen.
 */
export function groupByMonth(photos: readonly Photo[], locale?: string): MonthGroup[] {
  const groups: MonthGroup[] = [];

  for (const photo of photos) {
    const key = photo.date.slice(0, 7);
    const last = groups.at(-1);

    if (last?.key === key) {
      last.photos.push(photo);
    } else {
      groups.push({ key, label: monthLabel(key, locale), photos: [photo] });
    }
  }

  return groups;
}
